import PaymentMethodOption from './PaymentMethodOption';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import AddressHelpers from '../../../services/helpers/AddressHelpers';
import { PaymentMethodProps } from './PaymentMethod';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import React, { useMemo, useState } from 'react';
import useAuth from '../../../hooks/global/useAuth';
import usePaymentCoordinator from '../../../hooks/payment/usePaymentCoordinator';
import PaymentDetails from './PaymentDetails';
import { useTranslation } from 'react-i18next';
import ConfirmCancelModal from '../../../modals/ConfirmCancelModal';
import PaymentManager from '../../../services/api/PaymentManager';
import toast from 'react-hot-toast';
import CloseIcon from '../../../assets/icons/actions/CloseIcon';
import Theme from '../../Theme';
import { Checkbox } from '@mui/material';

export default function CardPaymentMethod(props: PaymentMethodProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentMethods:CardPaymentMethod' });

  const { id } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const { isGuest } = useAuth();
  const { data } = useOrderFlow();
  const { loading } = usePaymentCoordinator();
  const {
    loading: blocked,
    selectedPaymentMethod,
    paymentOptions,
    shouldSavePaymentInfo,
    setShouldSavePaymentInfo,
    findSavedPaymentMethod,
  } = usePaymentFlow();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const savedCard = useMemo(() => findSavedPaymentMethod(id), [paymentOptions]);

  const addressRequired = company?.country !== 'DE';

  const onDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const deletePaymentMethod = async () => {
    setDeleting(true);
    const { success } = await PaymentManager.removePaymentOption(savedCard.id);
    setDeleting(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_delete_payment_method'));
    }

    toast.success(t('toasts.payment_method_deleted'));

    return setShowDeleteModal(false);
  };

  return (
    <PaymentMethodOption
      {...props}
      extra={
        <>
          {Boolean(savedCard) && (
            <span className="flex items-center">
              <PaymentDetails hideIcons={!savedCard.payment_method_brand} {...savedCard} />
              <div
                className="ml-xs border border-gray-300 rounded-full flex justify-center items-center w-5 h-5 cursor-pointer"
                onClick={onDeleteClick}
              >
                <CloseIcon className="h-2 w-2" color={Theme.color.gray['300']} />
              </div>
            </span>
          )}
          {!savedCard && props.extra}
        </>
      }
    >
      {selectedPaymentMethod === id && !savedCard && !loading && !blocked && (
        <div className="p-sm border bg-[#F5F5F5]">
          <PaymentElement
            options={{
              wallets: {
                applePay: 'never',
                googlePay: 'never',
              },
              fields: {
                billingDetails: {
                  email: 'auto',
                  name: 'auto',
                  phone: 'auto',
                  address: 'auto',
                },
              },
              defaultValues: {
                billingDetails: {
                  email: profile?.email ?? data.customer_email,
                  name: `${profile?.first_name ?? data.customer_first_name} ${profile?.last_name ?? data.customer_last_name}`,
                  phone: profile?.phone_number ?? data.customer_phone_number,
                  address: {
                    country: profile?.country ?? data.delivery_country,
                    postal_code: profile?.zip_code ?? data.delivery_zip_code,
                    state: profile?.state ?? data.delivery_state,
                    city: profile?.city ?? data.delivery_city,
                    line1:
                      profile?.address ??
                      AddressHelpers.setCountry(data.delivery_country).format({
                        street_name: data.delivery_street_name,
                        street_number: data.delivery_street_number,
                      }),
                  },
                },
              },
            }}
          />
          {addressRequired && (
            <AddressElement
              className="mt-xs"
              options={{
                mode: 'billing',
                fields: {
                  phone: 'never',
                },
                defaultValues: {
                  name: `${profile?.first_name ?? data.customer_first_name} ${profile?.last_name ?? data.customer_last_name}`,
                  address: {
                    country: profile?.country ?? data.delivery_country,
                    postal_code: profile?.zip_code ?? data.delivery_zip_code,
                    state: profile?.state ?? data.delivery_state,
                    city: profile?.city ?? data.delivery_city,
                    line1:
                      profile?.address ??
                      AddressHelpers.setCountry(data.delivery_country).format({
                        street_name: data.delivery_street_name,
                        street_number: data.delivery_street_number,
                      }),
                  },
                },
              }}
            />
          )}
          {!isGuest && (
            <div className="flex mt-sm">
              <Checkbox
                checked={shouldSavePaymentInfo}
                onChange={(e) => setShouldSavePaymentInfo(e.target.checked)}
                id="should-save-payment-info"
              />
              <label htmlFor="should-save-payment-info" className="ml-xs my-auto text-sm">
                {t('form.save_payment_info.label')}
              </label>
            </div>
          )}
        </div>
      )}
      <ConfirmCancelModal
        openModal={showDeleteModal}
        loading={deleting}
        title={t('modals.delete_payment_method.title')}
        primaryButtonTitle={t('buttons.confirm')}
        secondaryButtonTitle={t('buttons.cancel')}
        onConfirm={() => deletePaymentMethod()}
        onCancel={() => setShowDeleteModal(false)}
      />
    </PaymentMethodOption>
  );
}
